import { DIAG, DIRS8, FILES, KNIGHT, ORTHO } from './geometry';
import type { AbilityDef, AbilityKind, GameRules, PieceTypeDef, Placement, Ruleset } from './types';

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

/** `code` is the short word used for the ability in move notation. */
export const ABILITY_INFO: Record<AbilityKind, { name: string; icon: string; code: string; desc: string; targeted: boolean }> = {
  fireBreath: { name: 'Fire Breath', icon: '🔥', code: 'fire', targeted: true, desc: 'Burn one enemy in a straight line within range without moving.' },
  wingSweep: { name: 'Wing Sweep', icon: '💨', code: 'sweep', targeted: false, desc: 'Push every adjacent enemy one square away. Pieces pushed off the board or into holes are destroyed.' },
  detonate: { name: 'Detonate', icon: '💥', code: 'boom', targeted: false, desc: 'Explode, destroying every piece in the surrounding squares. Other bombs may chain.' },
  timedFuse: { name: 'Timed Fuse', icon: '⏲️', code: 'arm', targeted: false, desc: 'Arm the bomb. It detonates automatically after the set number of your turns.' },
  kick: { name: 'Kick', icon: '🦶', code: 'kick', targeted: true, desc: 'Push an adjacent piece one square in a straight line onto an empty square.' },
  placeMine: { name: 'Place Mine', icon: '💣', code: 'mine', targeted: true, desc: 'Hide a mine on an adjacent empty square. Anything that steps on it is destroyed.' },
  tunnel: { name: 'Tunnel', icon: '🕳️', code: 'tunnel', targeted: true, desc: 'Move exactly N squares in a straight line through anything (no capture).' },
  defuse: { name: 'Defuse', icon: '🧰', code: 'defuse', targeted: true, desc: 'Remove a mine on an adjacent square.' },
  dig: { name: 'Dig', icon: '⛏️', code: 'dig', targeted: true, desc: 'Turn an adjacent empty square into a hole for a few turns.' },
  tongue: { name: 'Tongue', icon: '👅', code: 'tongue', targeted: true, desc: 'Pull the first enemy in a straight line onto the square next to the frog. Into a hole means it is gone.' },
  plantHedge: { name: 'Plant Hedge', icon: '🌿', code: 'hedge', targeted: true, desc: 'Grow a hedge on an adjacent empty square. It blocks moves, fire and pushes for a few turns.' },
  tend: { name: 'Tend', icon: '🪴', code: 'tend', targeted: false, desc: 'Put out burning squares and fill dug holes around the gardener.' },
  stampede: { name: 'Stampede', icon: '🐘', code: 'charge', targeted: true, desc: 'Charge straight ahead, trampling every enemy in the way. Stops before own pieces, walls, hedges and holes.' },
  trumpet: { name: 'Trumpet', icon: '📯', code: 'trumpet', targeted: false, desc: 'Stun every adjacent enemy: they cannot act on their next turn.' },
  multiply: { name: 'Multiply', icon: '🪳', code: 'spawn', targeted: true, desc: 'Spawn a young copy on an adjacent empty square. Only a few of its young can be alive at once, and the young cannot multiply.' },
  feast: { name: 'Feast', icon: '🍲', code: 'feast', targeted: false, desc: 'Feed every adjacent ally: their cooldowns reset and exhaustion or stuns wear off. Needs at least one ally next to the piece.' },
  panToss: { name: 'Pan Toss', icon: '🍳', code: 'pan', targeted: true, desc: 'Throw a pan at the first enemy in a straight line: it is stunned for its next turn.' },
};

export const ABILITY_KINDS = Object.keys(ABILITY_INFO) as AbilityKind[];

export function defaultAbility(kind: AbilityKind): AbilityDef {
  switch (kind) {
    case 'fireBreath': return { kind, enabled: true, cooldown: 4, range: 3, exhausts: true };
    case 'wingSweep': return { kind, enabled: true, cooldown: 6, range: 1 };
    case 'detonate': return { kind, enabled: true, cooldown: 0, range: 1 };
    case 'timedFuse': return { kind, enabled: true, cooldown: 0, range: 1, duration: 2 };
    case 'kick': return { kind, enabled: true, cooldown: 0, range: 1 };
    case 'placeMine': return { kind, enabled: true, cooldown: 0, range: 1, max: 2 };
    case 'tunnel': return { kind, enabled: true, cooldown: 0, range: 2 };
    case 'defuse': return { kind, enabled: true, cooldown: 0, range: 1 };
    case 'dig': return { kind, enabled: true, cooldown: 4, range: 1, duration: 3 };
    case 'tongue': return { kind, enabled: true, cooldown: 3, range: 3 };
    case 'plantHedge': return { kind, enabled: true, cooldown: 2, range: 1, duration: 3, max: 2 };
    case 'tend': return { kind, enabled: true, cooldown: 2, range: 1 };
    case 'stampede': return { kind, enabled: true, cooldown: 4, range: 3 };
    case 'trumpet': return { kind, enabled: true, cooldown: 4, range: 1 };
    case 'multiply': return { kind, enabled: true, cooldown: 5, range: 1, max: 2 };
    case 'feast': return { kind, enabled: true, cooldown: 4, range: 1 };
    case 'panToss': return { kind, enabled: true, cooldown: 3, range: 2 };
  }
}

export const STANDARD_PIECES: Record<string, PieceTypeDef> = {
  king: {
    id: 'king', name: 'King', letter: 'K', icon: 'king', royal: true, value: 0, castles: true,
    moves: [{ kind: 'leap', vectors: DIRS8, max: 1, mode: 'both' }], abilities: [],
  },
  queen: {
    id: 'queen', name: 'Queen', letter: 'Q', icon: 'queen', value: 9,
    moves: [{ kind: 'slide', vectors: DIRS8, max: 0, mode: 'both' }], abilities: [],
  },
  rook: {
    id: 'rook', name: 'Rook', letter: 'R', icon: 'rook', value: 5, castlePartner: true,
    moves: [{ kind: 'slide', vectors: ORTHO, max: 0, mode: 'both' }], abilities: [],
  },
  bishop: {
    id: 'bishop', name: 'Bishop', letter: 'B', icon: 'bishop', value: 3,
    moves: [{ kind: 'slide', vectors: DIAG, max: 0, mode: 'both' }], abilities: [],
  },
  knight: {
    id: 'knight', name: 'Knight', letter: 'N', icon: 'knight', value: 3,
    moves: [{ kind: 'leap', vectors: KNIGHT, max: 1, mode: 'both' }], abilities: [],
  },
  pawn: {
    id: 'pawn', name: 'Pawn', letter: '', icon: 'pawn', value: 1, promotes: true,
    moves: [
      { kind: 'slide', vectors: [[0, 1]], max: 1, mode: 'move' },
      { kind: 'slide', vectors: [[0, 1]], max: 2, mode: 'move', firstMoveOnly: true, createsEnPassant: true },
      { kind: 'leap', vectors: [[1, 1], [-1, 1]], max: 1, mode: 'capture', enPassant: true },
    ],
    abilities: [],
  },
};

export const CUSTOM_PIECES: Record<string, PieceTypeDef> = {
  dragon: {
    id: 'dragon', name: 'Dragon', letter: 'D', icon: 'dragon', value: 6,
    // Flies up to two squares in any direction, over pieces.
    moves: [{ kind: 'slide', vectors: DIRS8, max: 2, jump: true, mode: 'both' }],
    abilities: [defaultAbility('fireBreath'), defaultAbility('wingSweep')],
  },
  bomb: {
    id: 'bomb', name: 'Bomb', letter: 'O', icon: 'bomb', value: 2,
    moves: [{ kind: 'slide', vectors: ORTHO, max: 1, mode: 'both' }],
    abilities: [defaultAbility('detonate'), defaultAbility('timedFuse'), defaultAbility('kick')],
  },
  miner: {
    id: 'miner', name: 'Miner', letter: 'M', icon: 'miner', value: 3, detectRadius: 1,
    moves: [{ kind: 'leap', vectors: DIRS8, max: 1, mode: 'both' }],
    abilities: [defaultAbility('placeMine'), defaultAbility('tunnel'), defaultAbility('defuse'), defaultAbility('dig')],
  },
  frog: {
    id: 'frog', name: 'Frog', letter: 'F', icon: 'frog', value: 4,
    moves: [
      // Hops exactly two squares in any direction, over whatever is in between.
      { kind: 'leap', vectors: [[2, 0], [-2, 0], [0, 2], [0, -2], [2, 2], [-2, 2], [2, -2], [-2, -2]], max: 1, mode: 'both' },
      // Or paddles one square straight, without capturing.
      { kind: 'leap', vectors: ORTHO, max: 1, mode: 'move' },
    ],
    abilities: [defaultAbility('tongue')],
  },
  gardener: {
    id: 'gardener', name: 'Gardener', letter: 'G', icon: 'gardener', value: 3,
    moves: [
      // Walks up to two squares diagonally along the garden rows, or one step straight.
      { kind: 'slide', vectors: DIAG, max: 2, mode: 'both' },
      { kind: 'leap', vectors: ORTHO, max: 1, mode: 'move' },
    ],
    abilities: [defaultAbility('plantHedge'), defaultAbility('tend')],
  },
  elephant: {
    id: 'elephant', name: 'Illegal Elephant', letter: 'E', icon: 'elephant', value: 6,
    moves: [
      // Jumps two squares diagonally over anything, or plods one square straight.
      { kind: 'leap', vectors: [[2, 2], [-2, 2], [2, -2], [-2, -2]], max: 1, mode: 'both' },
      { kind: 'leap', vectors: ORTHO, max: 1, mode: 'both' },
    ],
    abilities: [defaultAbility('stampede'), defaultAbility('trumpet')],
  },
  cockroach: {
    id: 'cockroach', name: 'Cockroach', letter: 'X', icon: 'cockroach', value: 2, extraLives: 1,
    moves: [
      // Crawls one square any way, or dashes up to three squares straight without capturing.
      { kind: 'leap', vectors: DIRS8, max: 1, mode: 'both' },
      { kind: 'slide', vectors: ORTHO, max: 3, mode: 'move' },
    ],
    abilities: [defaultAbility('multiply')],
  },
  cook: {
    id: 'cook', name: 'Cook', letter: 'C', icon: 'cook', value: 3,
    moves: [
      // One step diagonally, or a two-square jump straight across the kitchen.
      { kind: 'leap', vectors: DIAG, max: 1, mode: 'both' },
      { kind: 'leap', vectors: [[2, 0], [-2, 0], [0, 2], [0, -2]], max: 1, mode: 'both' },
    ],
    abilities: [defaultAbility('feast'), defaultAbility('panToss')],
  },
};

export const ALL_PIECES: Record<string, PieceTypeDef> = { ...STANDARD_PIECES, ...CUSTOM_PIECES };

export function defaultRules(): GameRules {
  return {
    winConditions: { checkmate: true, captureAllRoyals: true, captureAnyRoyal: false, reachSquare: false },
    reachSquares: { w: [], b: [] },
    reachTypes: [],
    check: true,
    castling: true,
    enPassant: true,
    promotion: { enabled: true, zone: 1, choices: ['queen', 'rook', 'bishop', 'knight'] },
    movesPerTurn: 1,
    draws: { stalemate: 'draw', fiftyMove: true, threefold: true, insufficientMaterial: true },
    clock: { initial: 600, increment: 0 },
    takebacks: true,
    scorchedEarth: false,
    scorchDuration: 2,
    chainReaction: true,
    allowSelfRoyalBlast: false,
    persistentMines: false,
  };
}

const LETTER_TO_TYPE: Record<string, string> = {
  K: 'king', Q: 'queen', R: 'rook', B: 'bishop', N: 'knight', P: 'pawn', D: 'dragon', O: 'bomb', M: 'miner', F: 'frog', G: 'gardener', E: 'elephant', X: 'cockroach', C: 'cook',
};

/** Build placements from rank strings (rank 1 first), mirrored for black. */
export function mirroredSetup(ranks: string[], height: number): Placement[] {
  const out: Placement[] = [];
  ranks.forEach((row, y) => {
    [...row].forEach((ch, x) => {
      const type = LETTER_TO_TYPE[ch.toUpperCase()];
      if (!type) return;
      out.push({ sq: FILES[x] + (y + 1), type, color: 'w' });
      out.push({ sq: FILES[x] + (height - y), type, color: 'b' });
    });
  });
  return out;
}

function make(
  id: string,
  name: string,
  description: string,
  width: number,
  height: number,
  ranks: string[],
  opts: { pieces?: string[]; rules?: Partial<GameRules>; patch?: (r: Ruleset) => void } = {},
): Ruleset {
  const used = new Set(['king', 'queen', 'rook', 'bishop', 'knight', 'pawn', ...(opts.pieces ?? [])]);
  const pieces: Record<string, PieceTypeDef> = {};
  for (const t of used) pieces[t] = clone(ALL_PIECES[t]);
  const rs: Ruleset = {
    schema: 1, id, name, description,
    board: { width, height, voids: [], walls: [] },
    pieces,
    setup: mirroredSetup(ranks, height),
    rules: { ...defaultRules(), ...clone(opts.rules ?? {}) },
  };
  opts.patch?.(rs);
  return rs;
}

export const PRESETS: Ruleset[] = [
  make('standard', 'Standard Chess', 'The classic game with full FIDE rules.', 8, 8, ['RNBQKBNR', 'PPPPPPPP']),
  make('illegal', 'Illegal Chess', '10×10 battlefield with every new piece: Dragon, Bomb, Miner, Frog, Gardener, Elephant, Cockroach and Cook.', 10, 10,
    ['RMNBQKBNGR', 'POFDCXEPOP'], { pieces: ['dragon', 'bomb', 'miner', 'frog', 'gardener', 'elephant', 'cockroach', 'cook'],
      patch: (r) => { r.rules.promotion.choices = ['queen', 'dragon', 'elephant', 'rook', 'bishop', 'knight', 'frog', 'cook', 'miner', 'gardener']; } }),
  make('garden', 'Frog Garden', 'Frogs hop and grab with their tongue, gardeners grow hedges.', 8, 8, ['RFBQKBGR', 'PPPPPPPP'], {
    pieces: ['frog', 'gardener'],
    patch: (r) => { r.rules.promotion.choices = ['queen', 'frog', 'rook', 'bishop', 'gardener']; },
  }),
  make('kitchen', 'Kitchen Chaos', 'Cooks instead of knights, cockroaches among the pawns.', 8, 8, ['RCBQKBCR', 'PXPPPPXP'], {
    pieces: ['cook', 'cockroach'],
    patch: (r) => { r.rules.promotion.choices = ['queen', 'rook', 'bishop', 'cook']; },
  }),
  make('knight-queen', 'Knight Queen', 'A queen that moves like a knight.', 8, 8, ['RNBQKBNR', 'PPPPPPPP'], {
    patch: (r) => {
      r.pieces.queen.name = 'Knight Queen';
      r.pieces.queen.moves = [{ kind: 'leap', vectors: KNIGHT, max: 1, mode: 'both' }];
      r.pieces.queen.value = 4;
    },
  }),
  make('double-move', 'Double Move', 'Every turn you make two moves.', 8, 8, ['RNBQKBNR', 'PPPPPPPP'], {
    rules: { movesPerTurn: 2 },
  }),
  make('bomb-squad', 'Bomb Squad', 'Half of the pawns are bombs. A bomb next to a king gives check.', 8, 8, ['RNBQKBNR', 'POPOOPOP'], {
    pieces: ['bomb'],
  }),
  make('king-hill', 'King of the Hill', 'Bring your king to the centre to win.', 8, 8, ['RNBQKBNR', 'PPPPPPPP'], {
    rules: {
      winConditions: { checkmate: true, captureAllRoyals: true, captureAnyRoyal: false, reachSquare: true },
      reachSquares: { w: ['d4', 'e4', 'd5', 'e5'], b: ['d4', 'e4', 'd5', 'e5'] },
    },
  }),
  make('mini', 'Mini 6×6', 'Fast 6×6 game without bishops.', 6, 6, ['RNQKNR', 'PPPPPP'], {
    rules: { castling: false, promotion: { enabled: true, zone: 1, choices: ['queen', 'rook', 'knight'] } },
    patch: (r) => {
      r.pieces.pawn.moves = r.pieces.pawn.moves.filter((m) => !m.firstMoveOnly);
    },
  }),
  make('dragon-lair', 'Dragon Lair', 'Kings with dragons, holes in the middle and scorched earth.', 8, 8, ['RNBDKBNR', 'PPPPPPPP'], {
    pieces: ['dragon'],
    rules: { scorchedEarth: true },
    patch: (r) => { r.board.voids = ['d4', 'e5']; r.board.walls = ['d5', 'e4']; },
  }),
];

export function getPreset(id: string): Ruleset {
  const p = PRESETS.find((r) => r.id === id) ?? PRESETS[0];
  return clone(p);
}

export const cloneRuleset = (r: Ruleset): Ruleset => clone(r);

/** JSON with sorted keys, so equal objects give equal strings (databases may reorder keys). */
function stable(v: unknown): string {
  if (Array.isArray(v)) return '[' + v.map(stable).join(',') + ']';
  if (v && typeof v === 'object') {
    return '{' + Object.keys(v).sort().filter((k) => (v as Record<string, unknown>)[k] !== undefined)
      .map((k) => JSON.stringify(k) + ':' + stable((v as Record<string, unknown>)[k])).join(',') + '}';
  }
  return JSON.stringify(v);
}

/** Identifies what a ruleset plays like (board, pieces, setup, rules), ignoring name and default clock. */
export function rulesKey(r: Ruleset): string {
  const setup = [...r.setup].sort((a, b) => (a.sq + a.color).localeCompare(b.sq + b.color));
  return stable({ b: r.board, p: r.pieces, s: setup, r: { ...r.rules, clock: null } });
}
